import { Auth0Provider } from '@auth0/auth0-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  RouterProvider,
  createRouter,
  createRootRoute,
  createRoute,
  redirect,
  stripSearchParams,
} from '@tanstack/react-router';

import AnalysisRunDetail from '@/components/AnalysisRunDetail';
import AnalysisRunsTable from '@/components/AnalysisRunsTable';
import AuthenticatedApp from '@/components/AuthenticatedApp';
import CostAnalyticsPage from '@/components/CostAnalyticsPage';
import CreateAnalysisRun from '@/components/CreateAnalysisRun';
import DashboardLayout from '@/components/DashboardLayout';
import FeedbackTable from '@/components/FeedbackTable';
import LogoutPage from '@/components/LogoutPage';
import { ToastProvider } from '@/components/ToastProvider';
import UserEdit from '@/components/UserEdit';
import UsersTable from '@/components/UsersTable';
import { auth0Config } from '@/config/auth0';
import { TeamPage } from '@/features/accounts/TeamPage';
import { TeamsPage } from '@/features/accounts/TeamsPage';
import { ActionCardFeedPage } from '@/features/action-card-feed/ActionCardFeedPage';
import AgentConfigsPage from '@/features/agent-configs/AgentConfigsPage';
import { BroadcastsPage } from '@/features/broadcasts/BroadcastsPage';
import ChurnAnalysisPage from '@/features/churn/ChurnAnalysisPage';
import { CutoverPage } from '@/features/cutover/CutoverPage';
import EvalCasesPage from '@/features/eval-cases/EvalCasesPage';
import { ImChannelProactivityExperimentsPage } from '@/features/im-channel-proactivity-experiments/ImChannelProactivityExperimentsPage';
import { OnlineEvalAdHocRunsPage } from '@/features/online-eval/OnlineEvalAdHocRunsPage';
import { OnlineEvalCategoriesPage } from '@/features/online-eval/OnlineEvalCategoriesPage';
import { OnlineEvalIssuesPage } from '@/features/online-eval/OnlineEvalIssuesPage';
import { ProactiveAgentsPage } from '@/features/proactive-agents/ProactiveAgentsPage';
import { ProactivityExperimentsPage } from '@/features/proactivity-experiments/ProactivityExperimentsPage';
import { PromptPage } from '@/features/prompt-overrides/PromptPage';
import { PromptsPage } from '@/features/prompt-overrides/PromptsPage';
import { SlackBotPage } from '@/features/slack-bot/SlackBotPage';
import ChatHistoryPage from '@/features/votes/ChatHistoryPage';
import ChatVoteByChatPage from '@/features/votes/ChatVoteByChatPage';
import EntityVoteDetailsPage from '@/features/votes/EntityVoteDetailsPage';
import EntityVotesTable from '@/features/votes/EntityVotesTable';
import MessageVotesTable from '@/features/votes/MessageVotesTable';
import VoteAggregatesPage from '@/features/votes/VoteAggregatesPage';
import { useUser } from '@/hooks/useUsers';
import {
  ANALYSIS_DETAIL_SEARCH_DEFAULTS,
  ANALYSIS_NEW_SEARCH_DEFAULTS,
  ANALYSIS_SEARCH_DEFAULTS,
  AGENT_CONFIGS_SEARCH_DEFAULTS,
  COST_ANALYTICS_SEARCH_DEFAULTS,
  ENTITY_VOTE_DETAILS_SEARCH_DEFAULTS,
  ENTITY_VOTES_SEARCH_DEFAULTS,
  FEEDBACK_SEARCH_DEFAULTS,
  MESSAGE_VOTE_DETAILS_SEARCH_DEFAULTS,
  MESSAGE_VOTES_SEARCH_DEFAULTS,
  PROACTIVE_ROUTINES_SEARCH_DEFAULTS,
  USER_EDIT_SEARCH_DEFAULTS,
  USERS_SEARCH_DEFAULTS,
  ONLINE_EVAL_ISSUES_SEARCH_DEFAULTS,
  VOTE_AGGREGATES_SEARCH_DEFAULTS,
  agentConfigsSearchSchema,
  analysisDetailSearchSchema,
  analysisNewSearchSchema,
  analysisSearchSchema,
  costAnalyticsSearchSchema,
  entityVoteDetailsSearchSchema,
  entityVotesSearchSchema,
  feedbackSearchSchema,
  messageVoteDetailsSearchSchema,
  messageVotesSearchSchema,
  numericIdSchema,
  onlineEvalIssuesSearchSchema,
  proactiveRoutinesSearchSchema,
  userEditSearchSchema,
  usersSearchSchema,
  voteAggregatesSearchSchema,
} from '@/lib/route-search';

const queryClient = new QueryClient();

// Create routes
const rootRoute = createRootRoute({
  component: AuthenticatedApp,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({
      to: '/feedback',
    });
  },
});

const feedbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/feedback',
  validateSearch: feedbackSearchSchema,
  search: {
    middlewares: [stripSearchParams(FEEDBACK_SEARCH_DEFAULTS)],
  },
  component: () => (
    <DashboardLayout>
      <FeedbackTable />
    </DashboardLayout>
  ),
});

const messageVotesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/message-votes',
  validateSearch: messageVotesSearchSchema,
  search: {
    middlewares: [stripSearchParams(MESSAGE_VOTES_SEARCH_DEFAULTS)],
  },
  component: () => (
    <DashboardLayout>
      <MessageVotesTable />
    </DashboardLayout>
  ),
});

const voteAggregatesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/vote-aggregates',
  validateSearch: voteAggregatesSearchSchema,
  search: {
    middlewares: [stripSearchParams(VOTE_AGGREGATES_SEARCH_DEFAULTS)],
  },
  component: () => (
    <DashboardLayout>
      <VoteAggregatesPage />
    </DashboardLayout>
  ),
});

const chatHistoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/message-votes/$voteId',
  validateSearch: messageVoteDetailsSearchSchema,
  search: {
    middlewares: [stripSearchParams(MESSAGE_VOTE_DETAILS_SEARCH_DEFAULTS)],
  },
  component: () => {
    const { voteId } = chatHistoryRoute.useParams();
    const navigate = chatHistoryRoute.useNavigate();
    const search = chatHistoryRoute.useSearch();
    return (
      <ChatHistoryPage
        voteId={numericIdSchema.parse(voteId)}
        isDiscussionOpen={search.discussion}
        onDiscussionOpenChange={(discussion) =>
          navigate({
            search: { ...search, discussion },
            replace: !discussion,
          })
        }
        onBack={() =>
          navigate({
            to: '/message-votes',
            search: search.returnTo ?? undefined,
          })
        }
      />
    );
  },
});

const downvoteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/downvote/$voteId',
  validateSearch: messageVoteDetailsSearchSchema,
  search: {
    middlewares: [stripSearchParams(MESSAGE_VOTE_DETAILS_SEARCH_DEFAULTS)],
  },
  component: () => {
    const { voteId } = downvoteRoute.useParams();
    const navigate = downvoteRoute.useNavigate();
    const search = downvoteRoute.useSearch();
    return (
      <ChatHistoryPage
        voteId={numericIdSchema.parse(voteId)}
        isDiscussionOpen={search.discussion}
        onDiscussionOpenChange={(discussion) =>
          navigate({
            search: { ...search, discussion },
            replace: !discussion,
          })
        }
        onBack={() =>
          navigate({
            to: '/message-votes',
            search: search.returnTo ?? undefined,
          })
        }
      />
    );
  },
});

const chatVoteByChatRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/message-votes/by-chat/$chatId/$messageId/$userId',
  validateSearch: messageVoteDetailsSearchSchema,
  search: {
    middlewares: [stripSearchParams(MESSAGE_VOTE_DETAILS_SEARCH_DEFAULTS)],
  },
  component: ChatVoteByChatPage,
});

const entityVotesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/entity-votes',
  validateSearch: entityVotesSearchSchema,
  search: {
    middlewares: [stripSearchParams(ENTITY_VOTES_SEARCH_DEFAULTS)],
  },
  component: () => (
    <DashboardLayout>
      <EntityVotesTable />
    </DashboardLayout>
  ),
});

const entityVoteDetailsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/entity-votes/$voteId',
  validateSearch: entityVoteDetailsSearchSchema,
  search: {
    middlewares: [stripSearchParams(ENTITY_VOTE_DETAILS_SEARCH_DEFAULTS)],
  },
  component: () => {
    const { voteId } = entityVoteDetailsRoute.useParams();
    const navigate = entityVoteDetailsRoute.useNavigate();
    const search = entityVoteDetailsRoute.useSearch();
    return (
      <EntityVoteDetailsPage
        voteId={numericIdSchema.parse(voteId)}
        isDiscussionOpen={search.discussion}
        onDiscussionOpenChange={(discussion) =>
          navigate({
            search: { ...search, discussion },
            replace: !discussion,
          })
        }
        onBack={() =>
          navigate({
            to: '/entity-votes',
            search: search.returnTo ?? undefined,
          })
        }
      />
    );
  },
});

const usersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/users',
  validateSearch: usersSearchSchema,
  search: {
    middlewares: [stripSearchParams(USERS_SEARCH_DEFAULTS)],
  },
  component: () => (
    <DashboardLayout>
      <UsersTable />
    </DashboardLayout>
  ),
});

function UserEditPage() {
  const { userId } = userEditRoute.useParams();
  const navigate = userEditRoute.useNavigate();
  const { returnTo } = userEditRoute.useSearch();
  const { data: user, isLoading } = useUser(numericIdSchema.parse(userId));

  if (isLoading || !user) {
    return (
      <DashboardLayout>
        <div className="text-center py-8">Loading user...</div>
      </DashboardLayout>
    );
  }

  const handleBack = () => {
    navigate({
      to: '/users',
      search: returnTo ?? undefined,
    });
  };

  return (
    <DashboardLayout>
      <UserEdit user={user} onBack={handleBack} />
    </DashboardLayout>
  );
}

const userEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/users/$userId',
  validateSearch: userEditSearchSchema,
  search: {
    middlewares: [stripSearchParams(USER_EDIT_SEARCH_DEFAULTS)],
  },
  component: UserEditPage,
});

const teamsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/teams',
  component: () => (
    <DashboardLayout>
      <TeamsPage />
    </DashboardLayout>
  ),
});

const teamRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/teams/$teamId',
  component: () => {
    const { teamId } = teamRoute.useParams();
    return (
      <DashboardLayout>
        <TeamPage key={teamId} teamId={teamId} />
      </DashboardLayout>
    );
  },
});

const analysisRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/analysis',
  validateSearch: analysisSearchSchema,
  search: {
    middlewares: [stripSearchParams(ANALYSIS_SEARCH_DEFAULTS)],
  },
  component: () => (
    <DashboardLayout>
      <AnalysisRunsTable />
    </DashboardLayout>
  ),
});

const analysisNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/analysis/new',
  validateSearch: analysisNewSearchSchema,
  search: {
    middlewares: [stripSearchParams(ANALYSIS_NEW_SEARCH_DEFAULTS)],
  },
  component: () => {
    const navigate = analysisNewRoute.useNavigate();
    const { returnTo } = analysisNewRoute.useSearch();
    return (
      <DashboardLayout>
        <CreateAnalysisRun
          onBack={() =>
            navigate({ to: '/analysis', search: returnTo ?? undefined })
          }
          onCreated={(runId) =>
            navigate({
              to: '/analysis/$runId',
              params: { runId },
              search: { returnTo, resultsPage: 1 },
            })
          }
        />
      </DashboardLayout>
    );
  },
});

const analysisDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/analysis/$runId',
  validateSearch: analysisDetailSearchSchema,
  search: {
    middlewares: [stripSearchParams(ANALYSIS_DETAIL_SEARCH_DEFAULTS)],
  },
  component: () => {
    const { runId } = analysisDetailRoute.useParams();
    const navigate = analysisDetailRoute.useNavigate();
    const search = analysisDetailRoute.useSearch();
    return (
      <DashboardLayout>
        <AnalysisRunDetail
          runId={runId}
          resultsPage={search.resultsPage}
          onResultsPageChange={(resultsPage) =>
            navigate({ search: { ...search, resultsPage } })
          }
          onBack={() =>
            navigate({
              to: '/analysis',
              search: search.returnTo ?? undefined,
            })
          }
        />
      </DashboardLayout>
    );
  },
});

const churnAnalysisRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/churn-analysis',
  component: () => (
    <DashboardLayout>
      <ChurnAnalysisPage />
    </DashboardLayout>
  ),
});

const imChannelProactivityExperimentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/im-channel-proactivity-experiments',
  beforeLoad: () => {
    throw redirect({
      to: '/proactive-routines',
      search: { section: 'experimental' },
      replace: true,
    });
  },
});

const proactiveRoutinesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/proactive-routines',
  validateSearch: proactiveRoutinesSearchSchema,
  search: {
    middlewares: [stripSearchParams(PROACTIVE_ROUTINES_SEARCH_DEFAULTS)],
  },
  component: () => {
    const { section } = proactiveRoutinesRoute.useSearch();
    const navigate = proactiveRoutinesRoute.useNavigate();
    return (
      <DashboardLayout>
        <ImChannelProactivityExperimentsPage
          key={section}
          section={section}
          onSectionChange={(section) => navigate({ search: { section } })}
        />
      </DashboardLayout>
    );
  },
});

const broadcastsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/broadcasts',
  validateSearch: proactiveRoutinesSearchSchema,
  search: {
    middlewares: [stripSearchParams(PROACTIVE_ROUTINES_SEARCH_DEFAULTS)],
  },
  component: () => {
    const { section } = broadcastsRoute.useSearch();
    const navigate = broadcastsRoute.useNavigate();
    return (
      <DashboardLayout>
        <BroadcastsPage
          key={section}
          section={section}
          onSectionChange={(section) => navigate({ search: { section } })}
        />
      </DashboardLayout>
    );
  },
});

const proactivityExperimentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/proactivity-experiments',
  component: () => (
    <DashboardLayout>
      <ProactivityExperimentsPage />
    </DashboardLayout>
  ),
});

const proactiveAgentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/proactive-agents',
  component: () => (
    <DashboardLayout>
      <ProactiveAgentsPage />
    </DashboardLayout>
  ),
});

const promptsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/prompts',
  component: () => (
    <DashboardLayout>
      <PromptsPage />
    </DashboardLayout>
  ),
});

const promptRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/prompts/$slug',
  component: () => {
    const { slug } = promptRoute.useParams();
    return (
      <DashboardLayout>
        <PromptPage key={slug} slug={slug} />
      </DashboardLayout>
    );
  },
});

const actionCardFeedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/action-card-feed',
  component: () => (
    <DashboardLayout>
      <ActionCardFeedPage />
    </DashboardLayout>
  ),
});

const costAnalyticsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cost-analytics',
  validateSearch: costAnalyticsSearchSchema,
  search: {
    middlewares: [stripSearchParams(COST_ANALYTICS_SEARCH_DEFAULTS)],
  },
  component: () => (
    <DashboardLayout>
      <CostAnalyticsPage />
    </DashboardLayout>
  ),
});

const agentConfigsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/agent-configs',
  validateSearch: agentConfigsSearchSchema,
  search: {
    middlewares: [stripSearchParams(AGENT_CONFIGS_SEARCH_DEFAULTS)],
  },
  component: () => (
    <DashboardLayout>
      <AgentConfigsPage />
    </DashboardLayout>
  ),
});

const evalCasesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/eval-cases',
  component: () => (
    <DashboardLayout>
      <EvalCasesPage />
    </DashboardLayout>
  ),
});

const onlineEvalIssuesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/online-eval/issues',
  validateSearch: onlineEvalIssuesSearchSchema,
  search: {
    middlewares: [stripSearchParams(ONLINE_EVAL_ISSUES_SEARCH_DEFAULTS)],
  },
  component: () => {
    const search = onlineEvalIssuesRoute.useSearch();
    const navigate = onlineEvalIssuesRoute.useNavigate();
    const { debugSkeletonIssueId, agentStrip, chartUnit, ...filters } = search;
    return (
      <DashboardLayout>
        <OnlineEvalIssuesPage
          filters={filters}
          chartSettings={{ agentStrip, chartUnit }}
          debugSkeletonIssueId={debugSkeletonIssueId || null}
          onFiltersChange={(nextFilters) =>
            navigate({ search: { ...search, ...nextFilters } })
          }
          onChartSettingsChange={(nextSettings) =>
            navigate({ search: { ...search, ...nextSettings } })
          }
          onDebugSkeletonIssueIdChange={(nextDebugSkeletonIssueId) =>
            navigate({
              search: {
                ...search,
                debugSkeletonIssueId: nextDebugSkeletonIssueId ?? '',
              },
            })
          }
        />
      </DashboardLayout>
    );
  },
});

const onlineEvalCategoriesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/online-eval/categories',
  component: () => (
    <DashboardLayout>
      <OnlineEvalCategoriesPage />
    </DashboardLayout>
  ),
});

const onlineEvalAdHocRunsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/online-eval/ad-hoc-runs',
  component: () => (
    <DashboardLayout>
      <OnlineEvalAdHocRunsPage />
    </DashboardLayout>
  ),
});

const cutoverRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cutover',
  component: () => (
    <DashboardLayout>
      <CutoverPage />
    </DashboardLayout>
  ),
});

const slackBotRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/slack-bot',
  component: () => (
    <DashboardLayout>
      <SlackBotPage />
    </DashboardLayout>
  ),
});

const logoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/logout',
  component: LogoutPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  feedbackRoute,
  messageVotesRoute,
  voteAggregatesRoute,
  chatHistoryRoute,
  downvoteRoute,
  chatVoteByChatRoute,
  entityVotesRoute,
  entityVoteDetailsRoute,
  analysisRoute,
  analysisNewRoute,
  analysisDetailRoute,
  churnAnalysisRoute,
  imChannelProactivityExperimentsRoute,
  proactiveRoutinesRoute,
  broadcastsRoute,
  proactivityExperimentsRoute,
  proactiveAgentsRoute,
  promptsRoute,
  promptRoute,
  actionCardFeedRoute,
  costAnalyticsRoute,
  agentConfigsRoute,
  evalCasesRoute,
  onlineEvalIssuesRoute,
  onlineEvalCategoriesRoute,
  onlineEvalAdHocRunsRoute,
  usersRoute,
  userEditRoute,
  teamsRoute,
  teamRoute,
  cutoverRoute,
  slackBotRoute,
  logoutRoute,
]);

const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

function App() {
  return (
    <Auth0Provider
      {...auth0Config}
      onRedirectCallback={(appState) =>
        router.history.replace(appState?.returnTo ?? '/')
      }
    >
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <RouterProvider router={router} />
        </ToastProvider>
      </QueryClientProvider>
    </Auth0Provider>
  );
}

export default App;
